import { useEffect, useMemo, useRef, useState } from 'react';
import ReactCrop, { centerCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    IconBrightness,
    IconCopy,
    IconCrop,
    IconEye,
    IconGripVertical,
    IconRestore,
    IconRotateClockwise,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import Hint from '../../../../../components/common/Hint';
import { createToolItemId } from '../../toolPageUtils';
import { renderPdfPageThumbnails } from '../shared/pdfPreviewEngine';
import { toDisplayableBlob } from '../../image/shared/heicDisplay';
import { renderPdfPagePreview } from '../crop/cropPdfEngine';
import { pageHasColorAdjust, normalizeColorAdjust, DEFAULT_COLOR_ADJUST, buildColorFilterCss } from '../shared/pdfPageEditEngine';
import { isImageFile } from './createPdfEngine';
import PdfPageThumb from '../../components/pdf/PdfPageThumb';
import PdfPageColorPanel from '../../components/pdf/PdfPageColorPanel';
import PerspectiveQuadCrop, {
    cornersToRegion,
    defaultNormalizedCorners,
} from '../../components/pdf/PerspectiveQuadCrop';
import { buildFlattenPreviewUrl, flattenCornersKey } from '../shared/flattenPreview';
import { fitImageStyle, useBodyScrollLock, useFitBox } from '../../components/image/useFullscreenStage';

// Tints shared with the image tools' card actions (ImageActionOverlay), so a crop
// or delete button looks the same on a PDF page as on a photo.
const PAGE_ACTION_TONE = {
    crop: 'text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/40',
    rotate: 'text-sky-600 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-950/40',
    color: 'text-teal-600 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-950/40',
    duplicate: 'text-violet-600 hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-950/40',
    delete: 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40',
};

// The same tint, filled — this edit is applied to the page.
const PAGE_ACTION_ACTIVE = {
    crop: 'bg-amber-500 text-white hover:bg-amber-600',
    color: 'bg-teal-500 text-white hover:bg-teal-600',
};

function percentCropToRegion(percentCrop) {
    if (!percentCrop?.width || !percentCrop?.height) return null;
    return {
        left: percentCrop.x / 100,
        top: percentCrop.y / 100,
        width: percentCrop.width / 100,
        height: percentCrop.height / 100,
    };
}

function SortablePageCard({
    page,
    thumb,
    flattenedSrc,
    selected,
    labels,
    onSelect,
    onView,
    onRotateRight,
    onCrop,
    onColor,
    onDuplicate,
    onToggleRemoved,
}) {
    const { removed, cropRegion, cropCorners, cropMode, rotation, brightness, contrast, saturation, grayscale, sepia } = page;
    const hasColor = pageHasColorAdjust(page);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: page.id });

    const ringClass = removed
        ? 'border-[#FFB7B1] ring-2 ring-[#FFB7B1]/35 dark:border-red-400 dark:ring-red-400/25'
        : selected || cropRegion || cropCorners || hasColor
            ? 'border-pdf-cat-solid ring-2 ring-pdf-cat-solid/20 dark:border-orange-500 dark:ring-orange-500/20'
            : 'border-[#ECE8E4] hover:border-pdf-cat-solid/40 dark:border-zinc-700';

    const cropBadge = cropMode === 'flatten' ? labels.flattened : labels.cropped;

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.55 : 1,
            }}
            className="group relative"
        >
            <div className={`w-full overflow-hidden rounded-xl border bg-white text-left shadow-[0_4px_12px_rgba(28,24,21,0.06)] transition-colors dark:bg-zinc-900 ${ringClass}`}>
                <div
                    className="relative cursor-pointer overflow-hidden"
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect(page.id)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(page.id); }
                    }}
                >
                    <PdfPageThumb
                        src={thumb}
                        alt={page.label}
                        cropRegion={cropMode === 'flatten' ? null : cropRegion}
                        cropCorners={cropMode === 'flatten' ? cropCorners : null}
                        flattenedSrc={cropMode === 'flatten' ? flattenedSrc : null}
                        rotation={rotation}
                        removed={removed}
                        brightness={brightness}
                        contrast={contrast}
                        saturation={saturation}
                        grayscale={grayscale}
                        sepia={sepia}
                        placeholder={page.kind === 'pdf-page' ? 'PDF' : '…'}
                    />

                    <div className="pointer-events-none absolute inset-0 bg-[#1C1815]/0 transition-colors group-hover:bg-[#1C1815]/10" />

                    {!removed ? (
                        <Hint label={labels.view}>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onView(page); }}
                                className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#3D3833] opacity-0 shadow-[0_4px_14px_rgba(28,24,21,0.25)] transition-opacity hover:bg-white group-hover:pointer-events-auto group-hover:opacity-100 dark:bg-zinc-900/95 dark:text-zinc-100"
                                aria-label={labels.view}
                            >
                                <IconEye size={26} stroke={1.7} />
                            </button>
                        </Hint>
                    ) : null}

                    <Hint label={labels.drag}>
                        <button
                            type="button"
                            className="absolute right-2 top-2 z-10 flex h-9 w-9 cursor-grab items-center justify-center rounded-lg border border-[#ECE8E4] bg-white/95 text-[#6B635C] opacity-0 shadow-sm transition-opacity group-hover:opacity-100 active:cursor-grabbing dark:border-zinc-600 dark:bg-zinc-900/95 dark:text-zinc-300"
                            aria-label={labels.drag}
                            onClick={(e) => e.stopPropagation()}
                            {...attributes}
                            {...listeners}
                        >
                            <IconGripVertical size={18} stroke={2} />
                        </button>
                    </Hint>

                    {removed ? (
                        <span className="absolute left-2 top-2 z-10 rounded-full bg-[#FF8D86] px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.02em] text-white shadow-sm">
                            {labels.removed}
                        </span>
                    ) : null}

                    {/* Same bar as the image tools' cards (ImageActionOverlay): small white
                        buttons, one tint per tool, always shown on touch. An edit that is
                        applied to this page fills its button with that tint. */}
                    <div className="absolute inset-x-0 bottom-0 z-10 flex flex-wrap items-center justify-center gap-1.5 bg-gradient-to-t from-[#1C1815]/75 to-transparent px-2 pb-2 pt-8 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                        {removed ? (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onToggleRemoved(page.id); }}
                                className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg bg-white/95 px-2.5 text-[12px] font-semibold text-red-600 shadow-sm transition-colors hover:bg-red-50 dark:bg-zinc-800/95 dark:text-red-400 dark:hover:bg-red-950/40"
                                aria-label={labels.restore}
                            >
                                <IconRestore size={15} stroke={1.9} />
                                {labels.restore}
                            </button>
                        ) : [
                            { id: 'crop', icon: IconCrop, label: labels.crop, onClick: () => onCrop(page), active: Boolean(cropRegion || cropCorners) },
                            // One rotate button: four clockwise turns cover every angle.
                            { id: 'rotate', icon: IconRotateClockwise, label: labels.rotateRight, onClick: () => onRotateRight(page.id) },
                            { id: 'color', icon: IconBrightness, label: labels.color, onClick: () => onColor(page), active: hasColor },
                            { id: 'duplicate', icon: IconCopy, label: labels.duplicate, onClick: () => onDuplicate(page.id) },
                            { id: 'delete', icon: IconTrash, label: labels.delete, onClick: () => onToggleRemoved(page.id) },
                        ].map(({ id, icon: Icon, label, onClick, active }) => (
                            <Hint label={label} key={id}>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onClick(); }}
                                    aria-label={label}
                                    aria-pressed={active === undefined ? undefined : active}
                                    className={`flex h-8 w-8 items-center justify-center rounded-lg shadow-sm transition-colors ${
                                        active ? PAGE_ACTION_ACTIVE[id] : `bg-white/95 dark:bg-zinc-800/95 ${PAGE_ACTION_TONE[id]}`
                                    }`}
                                >
                                    <Icon size={15} stroke={1.9} />
                                </button>
                            </Hint>
                        ))}
                    </div>
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-[#ECE8E4] px-2.5 py-2 dark:border-zinc-700">
                    <p className={`truncate text-[11.5px] font-semibold ${removed ? 'text-[#FF8D86] dark:text-red-300' : 'text-[#3D3833] dark:text-zinc-200'}`}>
                        {page.label}
                    </p>
                    {!removed && (cropRegion || cropCorners || hasColor) ? (
                        <div className="flex flex-none flex-wrap justify-end gap-1">
                            {(cropRegion || cropCorners) ? (
                                <span className="rounded-full bg-pdf-cat-tint px-2 py-0.5 text-[10px] font-semibold text-pdf-cat-solid dark:bg-orange-950/40 dark:text-orange-300">
                                    {cropBadge}
                                </span>
                            ) : null}
                            {hasColor ? (
                                <span className="rounded-full bg-pdf-cat-tint px-2 py-0.5 text-[10px] font-semibold text-pdf-cat-solid dark:bg-orange-950/40 dark:text-orange-300">
                                    {labels.colorAdjusted}
                                </span>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default function CreatePdfPageGrid({
    pages,
    selectedPageId,
    onPagesChange,
    onSelectPage,
    onThumbsChange,
}) {
    const { t } = useTranslation();
    const imgRef = useRef(null);
    const [thumbs, setThumbs] = useState({});
    const thumbsRef = useRef({});

    const [modalPage, setModalPage] = useState(null);
    const [modalMode, setModalMode] = useState('crop'); // 'crop' | 'view' | 'color'
    const [modalSrc, setModalSrc] = useState('');
    const [loadingModal, setLoadingModal] = useState(false);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [saveMode, setSaveMode] = useState('crop');
    const [flattenCorners, setFlattenCorners] = useState(null);
    const [flattenPreviews, setFlattenPreviews] = useState({});
    const flattenPreviewUrlsRef = useRef({});
    const [draftColor, setDraftColor] = useState(DEFAULT_COLOR_ADJUST);

    // Full-screen page modal: the page is fitted to a measured stage, so crop
    // handles stay in view and the page behind cannot scroll under a drag.
    const stageRef = useRef(null);
    const fitBox = useFitBox(stageRef, !!modalPage);
    useBodyScrollLock(!!modalPage);

    // No backdrop to click now that it fills the screen — Escape closes it.
    useEffect(() => {
        if (!modalPage) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setModalPage(null); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [modalPage]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;
    const thumbKey = (page) => `${fileKey(page.file)}-${page.kind}-${page.pageIndex}`;

    useEffect(() => {
        let cancelled = false;

        async function loadThumbs() {
            const next = { ...thumbsRef.current };
            let changed = false;

            const pageGroups = new Map();
            for (const page of pages) {
                const groupKey = `${fileKey(page.file)}-${page.kind}`;
                if (!pageGroups.has(groupKey)) {
                    pageGroups.set(groupKey, { file: page.file, kind: page.kind, pages: [] });
                }
                pageGroups.get(groupKey).pages.push(page);
            }

            for (const { file, kind, pages: groupedPages } of pageGroups.values()) {
                if (kind === 'image' || isImageFile(file)) {
                    const firstPage = groupedPages[0];
                    const key = thumbKey(firstPage);
                    if (!next[key]) {
                        // HEIC is decoded first — a raw .heic thumbnail paints nothing
                        // outside Safari.
                        const drawable = await toDisplayableBlob(file);
                        if (cancelled) return;
                        next[key] = URL.createObjectURL(drawable);
                        changed = true;
                    }
                    continue;
                }

                const missingPages = groupedPages.filter((page) => !next[thumbKey(page)]);
                if (!missingPages.length) continue;

                try {
                    const maxPageIndex = Math.max(...missingPages.map((page) => page.pageIndex)) + 1;
                    const rendered = await renderPdfPageThumbnails(file, {
                        maxPages: maxPageIndex,
                        thumbHeight: 280,
                    });

                    for (const page of missingPages) {
                        const match = rendered.find((entry) => entry.pageNumber === page.pageIndex + 1);
                        next[thumbKey(page)] = match?.src ?? null;
                        changed = true;
                    }
                } catch {
                    for (const page of missingPages) {
                        next[thumbKey(page)] = null;
                        changed = true;
                    }
                }
            }

            const pruned = {};
            for (const page of pages) {
                const key = thumbKey(page);
                if (next[key]) pruned[key] = next[key];
            }

            for (const [key, url] of Object.entries(thumbsRef.current)) {
                if (!pruned[key] && typeof url === 'string' && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            }

            if (!cancelled) {
                const didChange = changed || Object.keys(pruned).length !== Object.keys(thumbsRef.current).length;
                thumbsRef.current = pruned;
                if (didChange) {
                    setThumbs({ ...pruned });
                    onThumbsChange?.({ ...pruned });
                }
            }
        }

        loadThumbs();

        return () => {
            cancelled = true;
        };
    }, [pages]);

    useEffect(() => () => {
        for (const url of Object.values(thumbsRef.current)) {
            if (typeof url === 'string' && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        }
        thumbsRef.current = {};
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function syncFlattenPreviews() {
            const needed = {};
            for (const page of pages) {
                if (page.cropMode !== 'flatten' || page.cropCorners?.length !== 4) continue;
                const src = thumbs[thumbKey(page)];
                if (!src) continue;
                needed[page.id] = {
                    src,
                    key: `${src}|${flattenCornersKey(page.cropCorners)}`,
                    corners: page.cropCorners,
                };
            }

            const nextUrls = { ...flattenPreviewUrlsRef.current };
            for (const id of Object.keys(nextUrls)) {
                if (!needed[id] || nextUrls[id].key !== needed[id].key) {
                    URL.revokeObjectURL(nextUrls[id].url);
                    delete nextUrls[id];
                }
            }

            const jobs = [];
            for (const [id, meta] of Object.entries(needed)) {
                if (nextUrls[id]?.key === meta.key) continue;
                jobs.push((async () => {
                    try {
                        const url = await buildFlattenPreviewUrl(meta.src, meta.corners);
                        if (!url || cancelled) {
                            if (url) URL.revokeObjectURL(url);
                            return null;
                        }
                        return { id, url, key: meta.key };
                    } catch {
                        return null;
                    }
                })());
            }

            const results = jobs.length ? await Promise.all(jobs) : [];
            if (cancelled) {
                for (const r of results) {
                    if (r?.url) URL.revokeObjectURL(r.url);
                }
                return;
            }

            for (const r of results) {
                if (!r) continue;
                if (nextUrls[r.id]) URL.revokeObjectURL(nextUrls[r.id].url);
                nextUrls[r.id] = { url: r.url, key: r.key };
            }

            flattenPreviewUrlsRef.current = nextUrls;
            setFlattenPreviews(
                Object.fromEntries(Object.entries(nextUrls).map(([id, entry]) => [id, entry.url])),
            );
        }

        syncFlattenPreviews();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pages, thumbs]);

    useEffect(() => () => {
        for (const entry of Object.values(flattenPreviewUrlsRef.current)) {
            URL.revokeObjectURL(entry.url);
        }
        flattenPreviewUrlsRef.current = {};
    }, []);

    // Load a full-size preview for the view / crop modal.
    useEffect(() => {
        if (!modalPage) return undefined;

        let cancelled = false;
        let objectUrl = null;
        setLoadingModal(true);
        setModalSrc('');
        setCrop(undefined);
        setCompletedCrop(null);

        const load = async () => {
            try {
                if (modalPage.kind === 'image' || isImageFile(modalPage.file)) {
                    const drawable = await toDisplayableBlob(modalPage.file);
                    if (cancelled) return;
                    objectUrl = URL.createObjectURL(drawable);
                    if (!cancelled) setModalSrc(objectUrl);
                } else {
                    const src = await renderPdfPagePreview(modalPage.file, modalPage.pageIndex + 1);
                    if (!cancelled) setModalSrc(src);
                }
            } catch (err) {
                if (cancelled) return;
                const msg = err?.message === 'password'
                    ? t('pdfPageEditor.password')
                    : t('pdfPageEditor.previewFailed');
                notifications.show({ title: t('common.error'), message: msg, color: 'red' });
                setModalPage(null);
            } finally {
                if (!cancelled) setLoadingModal(false);
            }
        };

        load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [modalPage, t]);

    const labels = useMemo(() => ({
        view: t('pdfPageEditor.view'),
        rotateRight: t('pdfPageEditor.rotateRight'),
        crop: t('pdfPageEditor.crop'),
        cropped: t('pdfPageEditor.cropped'),
        flattened: t('pdfPageEditor.flattened'),
        color: t('pdfPageEditor.color'),
        colorAdjusted: t('pdfPageEditor.colorAdjusted'),
        duplicate: t('pdfPageEditor.duplicate'),
        delete: t('pdfPageEditor.delete'),
        removed: t('pdfPageEditor.removed'),
        restore: t('pdfPageEditor.restore'),
        drag: t('pdfPageEditor.drag'),
    }), [t]);

    const openCrop = (page) => {
        setSaveMode(page.cropMode === 'flatten' ? 'flatten' : 'crop');
        setFlattenCorners(
            page.cropCorners?.length === 4
                ? page.cropCorners.map((c) => ({ ...c }))
                : defaultNormalizedCorners(),
        );
        setModalMode('crop');
        setModalPage(page);
    };
    const openView = (page) => { setModalMode('view'); setModalPage(page); };
    const openColor = (page) => {
        setDraftColor(normalizeColorAdjust(page));
        setModalMode('color');
        setModalPage(page);
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        onPagesChange((prev) => {
            const oldIndex = prev.findIndex((p) => p.id === active.id);
            const newIndex = prev.findIndex((p) => p.id === over.id);
            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    const duplicatePage = (id) => {
        onPagesChange((prev) => {
            const index = prev.findIndex((p) => p.id === id);
            if (index < 0) return prev;
            const copy = { ...prev[index], id: createToolItemId(), removed: false };
            const next = [...prev];
            next.splice(index + 1, 0, copy);
            return next;
        });
    };

    const rotateRight = (id) => onPagesChange((prev) => prev.map((p) => (
        p.id === id ? { ...p, rotation: (((p.rotation || 0) + 90) % 360) } : p
    )));

    const toggleRemoved = (id) => {
        onPagesChange((prev) => prev.map((p) => (
            p.id === id ? { ...p, removed: !p.removed } : p
        )));
        if (selectedPageId === id) onSelectPage(null);
    };

    const handleImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const saved = modalPage?.cropPct;
        if (saved) {
            setCrop(saved);
            setCompletedCrop(saved);
            return;
        }
        const next = centerCrop({ unit: '%', width: 80, height: 80 }, width, height);
        setCrop(next);
        setCompletedCrop(next);
    };

    const saveCrop = () => {
        if (!modalPage) return;

        if (saveMode === 'flatten') {
            const corners = flattenCorners?.length === 4 ? flattenCorners : defaultNormalizedCorners();
            const region = cornersToRegion(corners);
            if (!region) {
                notifications.show({ title: t('common.error'), message: t('pdfPageEditor.noRegion'), color: 'red' });
                return;
            }
            onPagesChange((prev) => prev.map((p) => (
                p.id === modalPage.id
                    ? {
                        ...p,
                        cropRegion: region,
                        cropPct: null,
                        cropCorners: corners,
                        cropMode: 'flatten',
                    }
                    : p
            )));
            setModalPage(null);
            return;
        }

        const region = percentCropToRegion(completedCrop);
        if (!region) {
            notifications.show({ title: t('common.error'), message: t('pdfPageEditor.noRegion'), color: 'red' });
            return;
        }
        onPagesChange((prev) => prev.map((p) => (
            p.id === modalPage.id
                ? {
                    ...p,
                    cropRegion: region,
                    cropPct: completedCrop,
                    cropCorners: null,
                    cropMode: 'crop',
                }
                : p
        )));
        setModalPage(null);
    };

    const clearCrop = () => {
        if (!modalPage) return;
        onPagesChange((prev) => prev.map((p) => (
            p.id === modalPage.id
                ? { ...p, cropRegion: null, cropPct: null, cropCorners: null, cropMode: 'crop' }
                : p
        )));
        setModalPage(null);
    };

    const saveColor = () => {
        if (!modalPage) return;
        onPagesChange((prev) => prev.map((p) => (
            p.id === modalPage.id
                ? { ...p, ...normalizeColorAdjust(draftColor) }
                : p
        )));
        setModalPage(null);
    };

    const clearColor = () => {
        if (!modalPage) return;
        onPagesChange((prev) => prev.map((p) => (
            p.id === modalPage.id
                ? { ...p, ...DEFAULT_COLOR_ADJUST }
                : p
        )));
        setModalPage(null);
    };

    if (!pages.length) return null;

    return (
        <>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={pages.map((p) => p.id)} strategy={rectSortingStrategy}>
                    <div className="h-full min-h-0 overflow-auto px-1 py-1 [scrollbar-width:thin]">
                        <div className={`grid gap-4 ${pages.length === 1 ? 'mx-auto max-w-sm grid-cols-1' : 'grid-cols-3'}`}>
                            {pages.map((page) => (
                                <SortablePageCard
                                    key={page.id}
                                    page={page}
                                    thumb={thumbs[thumbKey(page)]}
                                    flattenedSrc={flattenPreviews[page.id]}
                                    selected={selectedPageId === page.id}
                                    labels={labels}
                                    onSelect={onSelectPage}
                                    onView={openView}
                                    onRotateRight={rotateRight}
                                    onCrop={openCrop}
                                    onColor={openColor}
                                    onDuplicate={duplicatePage}
                                    onToggleRemoved={toggleRemoved}
                                />
                            ))}
                        </div>
                    </div>
                </SortableContext>
            </DndContext>

            {modalPage ? (
                <div
                    className="fixed inset-0 z-[200] flex h-dvh w-screen flex-col overflow-hidden bg-white dark:bg-zinc-900"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <div className="flex flex-none items-center justify-between gap-3 border-b border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                            <p className="min-w-0 truncate text-sm font-semibold text-[#1C1815] dark:text-zinc-100">
                                {modalPage.label}
                            </p>
                            <button
                                type="button"
                                onClick={() => setModalPage(null)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#ECE8E4] text-[#6B635C] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:text-zinc-300"
                                aria-label={t('common.close')}
                            >
                                <IconX size={16} stroke={2} />
                            </button>
                        </div>

                        <div
                            ref={stageRef}
                            className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden bg-[#1C1815] p-3 sm:p-5 dark:bg-zinc-950"
                        >
                            {loadingModal ? (
                                <div className="flex h-full items-center justify-center text-sm text-white/70">
                                    {t('pdfPageEditor.loadingPreview')}
                                </div>
                            ) : modalSrc ? (
                                <div className="flex min-h-0 min-w-0 items-center justify-center">
                                    {modalMode === 'crop' ? (
                                        saveMode === 'flatten' ? (
                                            <PerspectiveQuadCrop
                                                key={`${modalPage.id}-flatten`}
                                                src={modalSrc}
                                                value={flattenCorners}
                                                onChange={setFlattenCorners}
                                                className=""
                                                imgStyle={fitImageStyle(fitBox)}
                                            />
                                        ) : (
                                            <ReactCrop
                                                crop={crop}
                                                onChange={(next) => setCrop(next)}
                                                onComplete={(_, percentCrop) => setCompletedCrop(percentCrop)}
                                                className="max-h-full max-w-full"
                                            >
                                                <img
                                                    ref={imgRef}
                                                    src={modalSrc}
                                                    alt=""
                                                    onLoad={handleImageLoad}
                                                    className="object-contain"
                                                    style={fitImageStyle(fitBox)}
                                                    draggable={false}
                                                />
                                            </ReactCrop>
                                        )
                                    ) : (
                                        <img
                                            src={modalSrc}
                                            alt=""
                                            className="object-contain shadow-[0_8px_28px_rgba(28,24,21,0.18)]"
                                            style={modalMode === 'color'
                                                ? { ...fitImageStyle(fitBox), filter: buildColorFilterCss(draftColor) }
                                                : fitImageStyle(fitBox)}
                                            draggable={false}
                                        />
                                    )}
                                </div>
                            ) : null}
                        </div>

                        {modalMode === 'crop' ? (
                            <div className="flex-none space-y-3 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-semibold text-[#6B635C] dark:text-zinc-400">
                                        {t('pdfPageEditor.saveAs')}
                                    </span>
                                    <div className="flex gap-1 rounded-[11px] bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                        <button
                                            type="button"
                                            onClick={() => setSaveMode('crop')}
                                            className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                                                saveMode === 'crop'
                                                    ? 'bg-white text-pdf-cat-solid shadow-sm dark:bg-zinc-900 dark:text-orange-400'
                                                    : 'text-[#6B635C] dark:text-zinc-400'
                                            }`}
                                        >
                                            {t('pdfPageEditor.modeCrop')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSaveMode('flatten');
                                                setFlattenCorners((prev) => (
                                                    prev?.length === 4 ? prev : defaultNormalizedCorners()
                                                ));
                                            }}
                                            className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                                                saveMode === 'flatten'
                                                    ? 'bg-white text-pdf-cat-solid shadow-sm dark:bg-zinc-900 dark:text-orange-400'
                                                    : 'text-[#6B635C] dark:text-zinc-400'
                                            }`}
                                        >
                                            {t('pdfPageEditor.modeFlatten')}
                                        </button>
                                    </div>
                                    <p className="w-full text-[11px] text-[#A8A099] dark:text-zinc-500">
                                        {saveMode === 'flatten'
                                            ? t('pdfPageEditor.modeFlattenHint')
                                            : t('pdfPageEditor.modeCropHint')}
                                    </p>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <div>
                                        {modalPage.cropRegion ? (
                                            <button
                                                type="button"
                                                onClick={clearCrop}
                                                className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-2 text-sm font-semibold text-[#D33A2C] transition-colors hover:border-red-200 dark:border-zinc-700 dark:bg-zinc-900"
                                            >
                                                {t('pdfPageEditor.removeCrop')}
                                            </button>
                                        ) : null}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setModalPage(null)}
                                            className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-2 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                        >
                                            {t('common.cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={saveCrop}
                                            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                                        >
                                            {t('common.save')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : modalMode === 'color' ? (
                            <PdfPageColorPanel
                                value={draftColor}
                                onChange={setDraftColor}
                                t={t}
                                onReset={clearColor}
                                onCancel={() => setModalPage(null)}
                                onSave={saveColor}
                                showReset={pageHasColorAdjust(draftColor) || pageHasColorAdjust(modalPage)}
                            />
                        ) : (
                            <div className="flex flex-none items-center justify-end gap-2 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                                <button
                                    type="button"
                                    onClick={() => setModalPage(null)}
                                    className="rounded-xl border border-[#ECE8E4] bg-white px-5 py-2 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                >
                                    {t('common.close')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </>
    );
}
